import { FC, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import Box from "@mui/material/Box";
import {
  Breadcrumb,
  type BreadcrumbItemConfig,
} from "@/components/ui/Breadcrumb";
import { Segment } from "@/apis/bacchus/types";
import { SegmentSwitcherDropdown } from "./SegmentSwitcherDropdown";

export type SegmentBreadcrumbMode = "browse" | "create" | "edit";

interface SegmentBreadcrumbProps {
  segmentId?: string;
  segmentName?: string;
  isLoading?: boolean;
  mode: SegmentBreadcrumbMode;
  allSegments: Segment[];
}

export const SegmentBreadcrumb: FC<SegmentBreadcrumbProps> = ({
  segmentId,
  segmentName,
  isLoading = false,
  mode,
  allSegments,
}) => {
  const navigate = useNavigate();
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const dropdownOpen = Boolean(dropdownAnchorEl);

  const handleSegmentsClick = useCallback(() => {
    navigate("/segments");
  }, [navigate]);

  const handleDropdownClick = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      setDropdownAnchorEl(event.currentTarget.parentElement);
    },
    []
  );

  const handleDropdownClose = useCallback(() => {
    setDropdownAnchorEl(null);
  }, []);

  const handleSegmentSelect = useCallback(
    (selectedSegmentId: string) => {
      navigate(`/segments/${selectedSegmentId}`);
    },
    [navigate]
  );

  // Build breadcrumb items based on mode
  const buildBreadcrumbItems = (): BreadcrumbItemConfig[] => {
    const items: BreadcrumbItemConfig[] = [];

    // First item: "Segments" - always clickable, has dropdown in edit mode
    items.push({
      label: "Segments",
      onClick: handleSegmentsClick,
      hasDropdown: mode === "edit",
      onDropdownClick: mode === "edit" ? handleDropdownClick : undefined,
    });

    // Second item: varies by mode
    const secondItem = match(mode)
      .with("browse", () => ({
        label: "Companies",
        isActive: true,
      }))
      .with("create", () => ({
        label: "New Segment",
        isActive: true,
      }))
      .with("edit", () => ({
        label: segmentName || "Loading...",
        isActive: true,
        isLoading,
      }))
      .exhaustive();

    items.push(secondItem);

    return items;
  };

  return (
    <Box id="segment-breadcrumb">
      <Breadcrumb items={buildBreadcrumbItems()} />

      {/* Dropdown rendered outside breadcrumb for proper positioning */}
      <SegmentSwitcherDropdown
        currentSegmentId={segmentId}
        allSegments={allSegments}
        anchorEl={dropdownAnchorEl}
        open={dropdownOpen}
        onClose={handleDropdownClose}
        onSelect={handleSegmentSelect}
      />
    </Box>
  );
};

export default SegmentBreadcrumb;
